import React from 'react'
import { PlatformConfig } from "@/types"
import { AtSign, Facebook, Globe, Instagram, Linkedin, MessageCircle, MessageSquare, Send, Store, Video, Youtube } from "lucide-react"

export const TwitterXIcon = ({ className, ...props }: React.SVGProps<SVGSVGElement>) =>
  React.createElement(
    'svg',
    {
      viewBox: '0 0 24 24',
      width: 24,
      height: 24,
      fill: 'currentColor',
      'aria-hidden': 'true',
      className,
      ...props,
    },
    React.createElement('path', {
      d: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
    })
  )

export const XIcon = TwitterXIcon

export const platformColors: Record<string, string> = {
  facebook: 'text-blue-600',
  instagram: 'text-rose-500',
  linkedin: 'text-blue-700',
  twitter: 'text-neutral-900 dark:text-white',
  x: 'text-neutral-900 dark:text-white',
  google: 'text-emerald-600',
  tiktok: 'text-pink-500',
  youtube: 'text-red-600',
  reddit: 'text-orange-500',
  threads: 'text-neutral-900 dark:text-white',
  whatsapp: 'text-emerald-500',
  telegram: 'text-sky-500',
  wordpress: 'text-blue-500',
}

export const platformBgColors: Record<string, string> = {
  facebook: 'bg-blue-600/10',
  instagram: 'bg-rose-500/10',
  linkedin: 'bg-blue-700/10',
  twitter: 'bg-neutral-900/10 dark:bg-white/10',
  x: 'bg-neutral-900/10 dark:bg-white/10',
  google: 'bg-emerald-600/10',
  tiktok: 'bg-pink-500/10',
  youtube: 'bg-red-600/10',
  reddit: 'bg-orange-500/10',
  threads: 'bg-neutral-900/10 dark:bg-white/10',
  whatsapp: 'bg-emerald-500/10',
  telegram: 'bg-sky-500/10',
  wordpress: 'bg-blue-500/10',
}

export const platforms: PlatformConfig[] = [
  {
    id: 'facebook',
    name: 'social_facebook',
    icon: Facebook,
    color: 'text-blue-600',
    bgColor: 'bg-blue-600/10',
    borderColor: 'border-blue-600/30',
    description: 'social_facebook_desc',
    features: ['Pages', 'Analytics'],
    isAvailable: true,
  },
  {
    id: 'instagram',
    name: 'social_instagram',
    icon: Instagram,
    color: 'text-rose-500',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/30',
    description: 'social_instagram_desc',
    features: ['Business', 'Insights'],
    isAvailable: true,
  },
  {
    id: 'linkedin',
    name: 'social_linkedin',
    icon: Linkedin,
    color: 'text-blue-700',
    bgColor: 'bg-blue-700/10',
    borderColor: 'border-blue-700/30',
    description: 'social_linkedin_desc',
    features: ['Profiles', 'Pages'],
    isAvailable: true,
  },
  {
    id: 'twitter',
    name: 'social_twitter',
    icon: TwitterXIcon,
    color: 'text-neutral-900 dark:text-white',
    bgColor: 'bg-neutral-900/10 dark:bg-white/10',
    borderColor: 'border-neutral-900/30 dark:border-white/30',
    description: 'social_twitter_desc',
    features: ['Posts', 'Analytics'],
    isAvailable: true,
  },
  {
    id: 'google',
    name: 'social_google',
    icon: Store,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-600/10',
    borderColor: 'border-emerald-600/30',
    description: 'social_google_desc',
    features: ['Locations', 'Local Posts'],
    isAvailable: true,
  },
  {
    id: 'tiktok',
    name: 'social_tiktok',
    icon: Video,
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
    description: 'social_tiktok_desc',
    features: ['Videos', 'Creator'],
    isAvailable: true,
  },
  {
    id: 'youtube',
    name: 'social_youtube',
    icon: Youtube,
    color: 'text-red-600',
    bgColor: 'bg-red-600/10',
    borderColor: 'border-red-600/30',
    description: 'social_youtube_desc',
    features: ['Videos', 'Channels', 'Analytics'],
    isAvailable: true,
  },
  {
    id: 'reddit',
    name: 'social_reddit',
    icon: MessageCircle,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    description: 'social_reddit_desc',
    features: ['Posts', 'Communities'],
    isAvailable: true,
  },
  {
    id: 'threads',
    name: 'social_threads',
    icon: AtSign,
    color: 'text-neutral-900 dark:text-white',
    bgColor: 'bg-neutral-900/10 dark:bg-white/10',
    borderColor: 'border-neutral-900/30 dark:border-white/30',
    description: 'social_threads_desc',
    features: ['Posts', 'Replies'],
    isAvailable: true,
  },
  {
    id: 'whatsapp',
    name: 'social_whatsapp',
    icon: MessageSquare,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    description: 'social_whatsapp_desc',
    features: ['Official API', 'QR Link'],
    isAvailable: true,
  },
  {
    id: 'telegram',
    name: 'social_telegram',
    icon: Send,
    color: 'text-sky-500',
    bgColor: 'bg-sky-500/10',
    borderColor: 'border-sky-500/30',
    description: 'social_telegram_desc',
    features: ['Channels', 'Groups', 'Bot Alerts'],
    isAvailable: true,
  },
  {
    id: 'wordpress',
    name: 'social_wordpress',
    icon: Globe,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    description: 'social_wordpress_desc',
    features: ['Blog Posts', 'Auto-Publish'],
    isAvailable: true,
  },
]

export const platformIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: TwitterXIcon,
  x: TwitterXIcon,
  google: Store,
  tiktok: Video,
  youtube: Youtube,
  reddit: MessageCircle,
  threads: AtSign,
  whatsapp: MessageSquare,
  telegram: Send,
  wordpress: Globe,
}

export const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export const multiplePlatformColors: Record<string, { color: string; bgColor: string; borderColor: string }> = {
  twitter: {
    color: 'text-neutral-900 dark:text-white',
    bgColor: 'bg-neutral-900/10 dark:bg-white/10',
    borderColor: 'border-neutral-900/30 dark:border-white/30',
  },
  x: {
    color: 'text-neutral-900 dark:text-white',
    bgColor: 'bg-neutral-900/10 dark:bg-white/10',
    borderColor: 'border-neutral-900/30 dark:border-white/30',
  },
  facebook: {
    color: 'text-facebook',
    bgColor: 'bg-facebook/10',
    borderColor: 'border-facebook/30',
  },
  instagram: {
    color: 'text-instagram',
    bgColor: 'bg-instagram/10',
    borderColor: 'border-instagram/30',
  },
  linkedin: {
    color: 'text-linkedin',
    bgColor: 'bg-linkedin/10',
    borderColor: 'border-linkedin/30',
  },
  google: {
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-600/10',
    borderColor: 'border-emerald-600/30',
  },
  tiktok: {
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
  },
  youtube: {
    color: 'text-red-600',
    bgColor: 'bg-red-600/10',
    borderColor: 'border-red-600/30',
  },
  reddit: {
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
  },
  threads: {
    color: 'text-neutral-900 dark:text-white',
    bgColor: 'bg-neutral-900/10 dark:bg-white/10',
    borderColor: 'border-neutral-900/30 dark:border-white/30',
  },
  whatsapp: {
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
  },
  telegram: {
    color: 'text-sky-500',
    bgColor: 'bg-sky-500/10',
    borderColor: 'border-sky-500/30',
  },
  wordpress: {
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
  },
}
